/**
 * Admin image uploads — the tournament banner (issue #22) and the site logo.
 * The bytes never pass through our API: it signs a one-shot URL, and the
 * browser PUTs straight to storage.
 */
import { IMAGE_EXTENSIONS, type ImageContentType } from "@alpha/shared";
import { api } from "./api";

interface PresignedUpload {
  uploadUrl: string;
  publicUrl: string;
  contentType: ImageContentType;
}

const ACCEPTED = Object.keys(IMAGE_EXTENSIONS) as ImageContentType[];

/** What the file picker offers, and what a dropped file is checked against. */
export const IMAGE_ACCEPT = ACCEPTED.join(",");

/** Presign route for a tournament's banner. */
export const bannerUploadEndpoint = (tournamentId: string) =>
  `/admin/tournaments/${tournamentId}/banner-upload`;

/** Presign route for the app-wide logo. No id: site settings are a singleton. */
export const LOGO_UPLOAD_ENDPOINT = "/admin/settings/logo-upload";

/** "5 MB", "1.4 MB", "812 KB" — for limits and for what the admin actually picked. */
export function formatBytes(bytes: number): string {
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) return `${Number.isInteger(mb) ? mb : mb.toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/**
 * Why this file cannot be uploaded, or null if it can. Checked before asking
 * for a URL so a wrong file costs a message, not a round trip — the server
 * validates the same things again, since nothing here is a security boundary.
 *
 * `maxBytes` is a parameter rather than a constant because the caps differ by
 * use: the logo is fetched on every page by every visitor, the banner on one.
 */
export function validateImageFile(
  file: { type: string; size: number },
  maxBytes: number,
): string | null {
  if (!ACCEPTED.includes(file.type as ImageContentType)) {
    return "Choose a JPEG, PNG, WebP, or GIF image.";
  }
  if (file.size === 0) return "That file is empty.";
  if (file.size > maxBytes) {
    return `Images must be under ${formatBytes(maxBytes)} — that one is ${formatBytes(file.size)}.`;
  }
  return null;
}

/**
 * Upload an image through the presign route at `endpoint` and return the URL it
 * will be served from. Saving that URL onto a record is the caller's job, so an
 * abandoned upload changes nothing.
 */
export async function uploadImage(endpoint: string, file: File): Promise<string> {
  const signed = await api.post<PresignedUpload>(endpoint, {
    contentType: file.type,
    size: file.size,
  });

  // Deliberately not through lib/api: that helper JSON-encodes the body and
  // attaches the session token, and an Authorization header is not part of
  // what S3 signed — it rejects the request rather than ignoring the header.
  const res = await fetch(signed.uploadUrl, {
    method: "PUT",
    headers: { "content-type": signed.contentType },
    body: file,
  });
  if (!res.ok) throw new Error(`Image upload failed (${res.status})`);

  return signed.publicUrl;
}
